// frontend/src/components/ErrorBoundary.js
import React from 'react';
import '../styles/ErrorBoundary.css';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null, 
            errorInfo: null,
            errorId: null 
        };
    }

    static getDerivedStateFromError(error) {
        // Обновляет состояние, чтобы показать fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Логируем ошибку
        const errorId = Date.now().toString(36) + Math.random().toString(36).substr(2);
        
        console.error('🚨 Error Boundary поймал ошибку:', {
            errorId,
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            timestamp: new Date().toISOString()
        });

        // Сохраняем детали ошибки в state
        this.setState({
            error,
            errorInfo,
            errorId
        });

        // Можно отправить ошибку в сервис мониторинга
        this.reportErrorToService(error, errorInfo, errorId);
    }

    reportErrorToService = (error, errorInfo, errorId) => {
        // В production здесь можно интегрировать Sentry, LogRocket и т.д.
        if (process.env.NODE_ENV === 'production') {
            // Отправка в сервис мониторинга
            console.log('Отправка ошибки в сервис мониторинга:', { errorId, error: error.message });
        }
    };

    handleRetry = () => {
        // Сброс состояния ошибки для повторной попытки
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: null
        });
    };

    handleReload = () => {
        // Полная перезагрузка страницы
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            const { error, errorId } = this.state;
            const { fallback, showDetails = false } = this.props;

            // Если предоставлен кастомный fallback компонент
            if (fallback) {
                return fallback({ 
                    error, 
                    errorId, 
                    retry: this.handleRetry,
                    reload: this.handleReload 
                });
            }

            // Дефолтный UI для ошибки
            return (
                <div className="error-boundary">
                    <div className="error-boundary-content card">
                        <div className="error-icon">⚠️</div>
                        <h2>Щось пішло не так</h2>
                        <p>Сталася неочікувана помилка. Ми вже працюємо над її виправленням.</p>
                        
                        {showDetails && error && (
                            <details className="error-details">
                                <summary>Технічні деталі</summary>
                                <div className="error-content">
                                    <p><strong>ID помилки:</strong> {errorId}</p>
                                    <p><strong>Повідомлення:</strong> {error.message}</p>
                                    {process.env.NODE_ENV === 'development' && (
                                        <pre className="error-stack">{error.stack}</pre>
                                    )}
                                </div>
                            </details>
                        )}

                        <div className="error-actions">
                            <button 
                                onClick={this.handleRetry} 
                                className="btn-pill primary"
                            >
                                Спробувати знову
                            </button>
                            <button 
                                onClick={this.handleReload} 
                                className="btn-pill secondary"
                            >
                                Перезавантажити сторінку
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// HOC для оборачивания компонентов в Error Boundary
export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
    const WithErrorBoundaryComponent = (props) => (
        <ErrorBoundary {...errorBoundaryProps}>
            <WrappedComponent {...props} />
        </ErrorBoundary>
    );

    WithErrorBoundaryComponent.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;
    
    return WithErrorBoundaryComponent;
};

// Hook для использования Error Boundary в функциональных компонентах
export const useErrorHandler = () => {
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (error) {
            throw error;
        }
    }, [error]);

    const resetError = React.useCallback(() => {
        setError(null);
    }, []);

    const captureError = React.useCallback((error) => {
        console.error('🚨 Ошибка перехвачена useErrorHandler:', error);
        setError(error);
    }, []);

    return { captureError, resetError };
};

export default ErrorBoundary;
